// Fase 17.3, Plano 08 — modelos de roteiro (D-01, D-17, D-22): o PCP edita a sequência que várias peças compartilham,
// vê quantas peças seguem e quantas têm ajuste próprio ANTES de salvar, e a edição vira versão nova só para quem segue o modelo.
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ImpactoModeloRoteiroDto, ModeloRoteiroResumoDto, ServicoComCelulasDto } from '@pieltec/contracts';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { CampoSelect } from '../../../components/ui/campo-select';
import { Card, CardContent } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../../components/ui/form-dialog';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { Textarea } from '../../../components/ui/textarea';
import { etapasDaVersao, etapasParaSalvar, novaEtapa, validarEtapas, type EtapaEdicao } from '../../../lib/roteiros/etapas';
import { alterarModelo, atualizarCabecalhoDoModelo, criarModelo, impactoDoModelo, listarModelosDaGestao } from '../../../lib/roteiros/modelos-api';
import { assinaturaDasEtapas, mensagemDoResultado, resumoImpacto } from '../../../lib/roteiros/modelos';
import { servicosComCelulas } from '../../../lib/roteiros/roteiros-api';
import { obterSessaoAtual } from '../../../lib/session';
import { EditorEtapas } from './editor-etapas';

const MOTIVO_MINIMO = 5;
const CABECALHO = 'px-2 py-2 text-xs font-semibold uppercase tracking-wider';
const NOME_MAXIMO = 120;

interface NovoModelo {
  readonly nome: string;
  readonly motivo: string;
  readonly etapas: EtapaEdicao[];
  /** Id do modelo de origem na opção "Duplicar de outro modelo"; '' = começar do zero. */
  readonly origemId: string;
}

export function ModelosRoteiroPage() {
  const [modelos, setModelos] = useState<readonly ModeloRoteiroResumoDto[] | null>(null);
  const [servicos, setServicos] = useState<readonly ServicoComCelulasDto[]>([]);
  const [erroCarregar, setErroCarregar] = useState<string | null>(null);
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);
  const [selecionadoId, setSelecionadoId] = useState<string | null>(null);
  const [edicao, setEdicao] = useState<EtapaEdicao[] | null>(null);
  const [mostrarErros, setMostrarErros] = useState(false);
  const [impacto, setImpacto] = useState<ImpactoModeloRoteiroDto | null>(null);
  const [confirmarAberto, setConfirmarAberto] = useState(false);
  const [verificandoImpacto, setVerificandoImpacto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [novo, setNovo] = useState<NovoModelo | null>(null);
  const [mostrarErrosNovo, setMostrarErrosNovo] = useState(false);
  const [criando, setCriando] = useState(false);
  const [erroNovo, setErroNovo] = useState<string | null>(null);
  const [renomearAberto, setRenomearAberto] = useState(false);
  const [nomeNovo, setNomeNovo] = useState('');
  const [ocupadoCabecalho, setOcupadoCabecalho] = useState(false);
  const [erroCabecalho, setErroCabecalho] = useState<string | null>(null);

  // A autorização real é do servidor; aqui o botão de edição só aparece para quem pode (D-16).
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo && sessao.autenticado) setPermissoes(sessao.usuario.permissions);
    });
    return () => { vivo = false; };
  }, []);

  const carregar = useCallback(async (sinal?: { vivo: boolean }) => {
    setErroCarregar(null);
    try {
      const [lista, servicosCarregados] = await Promise.all([listarModelosDaGestao(mostrarInativos), servicosComCelulas()]);
      if (sinal && !sinal.vivo) return;
      setModelos(lista);
      setServicos(servicosCarregados);
    } catch {
      if (!sinal || sinal.vivo) setErroCarregar('Não foi possível carregar os modelos. Verifique a conexão e tente novamente.');
    }
  }, [mostrarInativos]);

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => { sinal.vivo = false; };
  }, [carregar]);

  const podeGerenciar = permissoes.includes('roteiros:gerenciar');
  const selecionado = useMemo(() => modelos?.find((modelo) => modelo.id === selecionadoId) ?? null, [modelos, selecionadoId]);
  const etapasDoSelecionado = useMemo(() => (selecionado ? etapasDaVersao(selecionado.etapas, servicos) : []), [selecionado, servicos]);

  const selecionar = (modelo: ModeloRoteiroResumoDto): void => {
    setSelecionadoId(modelo.id);
    setEdicao(null);
    setMostrarErros(false);
    setSucesso(null);
  };

  const iniciarEdicao = (): void => {
    if (!selecionado) return;
    setSucesso(null);
    setMostrarErros(false);
    setEdicao(etapasDaVersao(selecionado.etapas, servicos).length > 0 ? etapasDaVersao(selecionado.etapas, servicos) : servicos[0] ? [novaEtapa(servicos[0])] : []);
  };

  // Antes de abrir a confirmação, busca o impacto: o PCP vê quantas peças recebem a versão nova (D-17).
  const pedirSalvar = async (): Promise<void> => {
    if (!edicao || !selecionado) return;
    if (validarEtapas(edicao).length > 0) { setMostrarErros(true); return; }
    setVerificandoImpacto(true);
    setErroSalvar(null);
    try {
      setImpacto(await impactoDoModelo(selecionado.id));
      setConfirmarAberto(true);
    } catch {
      setErroSalvar('Não foi possível calcular quantas peças seguem este modelo. Tente novamente.');
    } finally {
      setVerificandoImpacto(false);
    }
  };

  const confirmarSalvar = async (): Promise<void> => {
    if (!edicao || !selecionado) return;
    setSalvando(true);
    setErroSalvar(null);
    try {
      const resultado = await alterarModelo(selecionado.id, { etapas: etapasParaSalvar(edicao), motivo: motivo.trim(), versaoEsperada: selecionado.versaoVigente ?? 0 });
      setConfirmarAberto(false);
      setMotivo('');
      setEdicao(null);
      setMostrarErros(false);
      await carregar();
      setSucesso(mensagemDoResultado(resultado));
    } catch (erro) {
      setErroSalvar(erro instanceof Error ? erro.message : 'Não foi possível salvar o modelo. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const motivoValido = motivo.trim().length >= MOTIVO_MINIMO;

  const iniciarNovo = (): void => {
    setSucesso(null);
    setErroNovo(null);
    setMostrarErrosNovo(false);
    setEdicao(null);
    setNovo({ nome: '', motivo: '', origemId: '', etapas: servicos[0] ? [novaEtapa(servicos[0])] : [] });
  };

  // "Duplicar de outro modelo": copia as etapas do modelo escolhido para o editor; nada é gravado até "Criar modelo".
  const duplicarDe = (origemId: string): void => {
    setNovo((atual) => {
      if (!atual) return atual;
      const origem = modelos?.find((modelo) => modelo.id === origemId);
      const etapas = origem ? etapasDaVersao(origem.etapas, servicos) : servicos[0] ? [novaEtapa(servicos[0])] : [];
      return { ...atual, origemId, etapas };
    });
  };

  const confirmarCriar = async (): Promise<void> => {
    if (!novo) return;
    const nome = novo.nome.trim();
    if (nome === '' || novo.motivo.trim().length < MOTIVO_MINIMO || validarEtapas(novo.etapas).length > 0) { setMostrarErrosNovo(true); return; }
    setCriando(true);
    setErroNovo(null);
    try {
      const resultado = await criarModelo({ nome, etapas: etapasParaSalvar(novo.etapas), motivo: novo.motivo.trim() });
      setNovo(null);
      setMostrarErrosNovo(false);
      await carregar();
      setSelecionadoId(resultado.modeloId);
      setSucesso(`Modelo ${resultado.codigo} criado.`);
    } catch (erro) {
      setErroNovo(erro instanceof Error ? erro.message : 'Não foi possível criar o modelo. Tente novamente.');
    } finally {
      setCriando(false);
    }
  };

  const abrirRenomear = (): void => {
    if (!selecionado) return;
    setNomeNovo(selecionado.nome);
    setErroCabecalho(null);
    setRenomearAberto(true);
  };

  const alterarCabecalho = async (alteracoes: { nome?: string; ativo?: boolean }, mensagem: string): Promise<void> => {
    if (!selecionado) return;
    setOcupadoCabecalho(true);
    setErroCabecalho(null);
    try {
      await atualizarCabecalhoDoModelo(selecionado.id, alteracoes);
      setRenomearAberto(false);
      await carregar();
      setSucesso(mensagem);
    } catch (erro) {
      setErroCabecalho(erro instanceof Error ? erro.message : 'Não foi possível atualizar o modelo. Tente novamente.');
    } finally {
      setOcupadoCabecalho(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Card>
        <CardContent className="flex flex-col gap-2">
          <a className="text-sm text-muted-foreground underline-offset-4 hover:underline" href="/pcp/roteiros">Roteiros das peças</a>
          <h1 className="text-[28px] font-semibold leading-tight">Modelos de roteiro</h1>
          <p className="text-sm text-muted-foreground">
            Um modelo é a sequência que várias peças compartilham. Ao salvar uma mudança, as peças que seguem o modelo recebem uma versão nova; peças com ajuste próprio e OPs já liberadas não mudam.
          </p>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <label htmlFor="modelos-inativos" className="flex items-center gap-2 text-sm">
              <Checkbox id="modelos-inativos" checked={mostrarInativos} onCheckedChange={(marcado) => setMostrarInativos(marcado === true)} />
              Mostrar modelos inativos
            </label>
            {podeGerenciar && novo === null ? <Button type="button" onClick={iniciarNovo}>Novo modelo</Button> : null}
          </div>
        </CardContent>
      </Card>

      {erroCarregar ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-destructive">{erroCarregar}</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>Tentar novamente</Button>
          </CardContent>
        </Card>
      ) : modelos === null ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando modelos">
          {Array.from({ length: 6 }).map((_, indice) => <Skeleton key={indice} className="h-8 w-full" />)}
        </div>
      ) : modelos.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <p className="text-sm font-semibold">Nenhum modelo de roteiro cadastrado.</p>
            <p className="text-sm text-muted-foreground">Os modelos nascem da importação do CargaMáquina ou de "Novo modelo".</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="gap-0 overflow-hidden py-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Modelos de roteiro</caption>
              <thead className="bg-muted text-left">
                <tr>
                  {['Código', 'Modelo', 'Sequência', 'Versão', 'Peças que seguem', 'Peças que diferem', ''].map((titulo) => (
                    <th key={titulo || 'acao'} scope="col" className={CABECALHO}>{titulo}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modelos.map((modelo) => (
                  <tr key={modelo.id} className={`border-t ${modelo.id === selecionadoId ? 'bg-muted' : ''} ${modelo.ativo ? '' : 'text-muted-foreground'}`}>
                    <td className="px-2 py-2 font-semibold">{modelo.codigo}</td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span>{modelo.nome}</span>
                        {modelo.ativo ? null : <Badge variant="outline">Inativo</Badge>}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-muted-foreground">{modelo.assinatura.split('>').join(' › ')}</td>
                    <td className="px-2 py-2">{modelo.versaoVigente === null ? <span className="text-muted-foreground">—</span> : `v${modelo.versaoVigente}`}</td>
                    <td className="px-2 py-2">{modelo.pecasQueSeguem}</td>
                    <td className="px-2 py-2">{modelo.pecasQueDiferem}</td>
                    <td className="px-2 py-2 text-right">
                      <Button type="button" variant="outline" size="sm" aria-pressed={modelo.id === selecionadoId} onClick={() => selecionar(modelo)}>
                        {modelo.id === selecionadoId ? 'Selecionado' : 'Ver etapas'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {novo !== null ? (
        <Card>
          <CardContent className="flex flex-col gap-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider">Novo modelo</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label htmlFor="novo-modelo-nome" className="text-sm font-semibold">Nome do modelo</label>
                <Input id="novo-modelo-nome" value={novo.nome} maxLength={NOME_MAXIMO} onChange={(evento) => setNovo({ ...novo, nome: evento.target.value })} />
                {mostrarErrosNovo && novo.nome.trim() === '' ? <p role="alert" className="text-xs text-destructive">Informe o nome do modelo.</p> : null}
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="novo-modelo-origem" className="text-sm font-semibold">Duplicar de outro modelo</label>
                <CampoSelect
                  id="novo-modelo-origem"
                  value={novo.origemId}
                  onValueChange={duplicarDe}
                  options={[{ value: '', label: 'Começar do zero' }, ...(modelos ?? []).filter((modelo) => modelo.ativo).map((modelo) => ({ value: modelo.id, label: `${modelo.codigo} — ${modelo.nome}` }))]}
                />
              </div>
            </div>
            <EditorEtapas etapas={novo.etapas} servicos={servicos} onChange={(etapas) => setNovo({ ...novo, etapas })} mostrarErros={mostrarErrosNovo} />
            {novo.etapas.length > 0 ? <p className="text-sm text-muted-foreground">Sequência: {assinaturaDasEtapas(novo.etapas, servicos)}</p> : null}
            <div className="flex flex-col gap-1">
              <label htmlFor="novo-modelo-motivo" className="text-sm font-semibold">Motivo</label>
              <Textarea id="novo-modelo-motivo" value={novo.motivo} maxLength={500} rows={2} onChange={(evento) => setNovo({ ...novo, motivo: evento.target.value })} />
              <p className={`text-xs ${mostrarErrosNovo && novo.motivo.trim().length < MOTIVO_MINIMO ? 'text-destructive' : 'text-muted-foreground'}`}>Mínimo de {MOTIVO_MINIMO} caracteres.</p>
            </div>
            {erroNovo ? <p role="alert" className="text-sm text-destructive">{erroNovo}</p> : null}
            <div className="flex flex-wrap gap-2">
              <Button type="button" disabled={criando} onClick={() => void confirmarCriar()}>{criando ? 'Criando...' : 'Criar modelo'}</Button>
              <Button type="button" variant="outline" disabled={criando} onClick={() => { setNovo(null); setMostrarErrosNovo(false); setErroNovo(null); }}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {selecionado ? (
        <Card>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex flex-col gap-1">
                <h2 className="text-sm font-semibold uppercase tracking-wider">{selecionado.codigo} · {selecionado.nome}</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{selecionado.versaoVigente === null ? 'Sem versão' : `Versão ${selecionado.versaoVigente}`}</Badge>
                  {selecionado.ativo ? null : <Badge variant="outline">Inativo</Badge>}
                  <span className="text-sm text-muted-foreground">{selecionado.pecasQueSeguem} peça(s) seguem · {selecionado.pecasQueDiferem} com ajuste próprio</span>
                </div>
              </div>
              {podeGerenciar && edicao === null ? (
                <div className="flex flex-wrap gap-2">
                  <Button type="button" onClick={iniciarEdicao}>Editar modelo</Button>
                  <Button type="button" variant="outline" disabled={ocupadoCabecalho} onClick={abrirRenomear}>Renomear</Button>
                  <Button type="button" variant="outline" disabled={ocupadoCabecalho} onClick={() => void alterarCabecalho({ ativo: !selecionado.ativo }, selecionado.ativo ? `Modelo ${selecionado.codigo} desativado.` : `Modelo ${selecionado.codigo} ativado.`)}>
                    {selecionado.ativo ? 'Desativar' : 'Ativar'}
                  </Button>
                </div>
              ) : null}
            </div>
            {sucesso ? <p role="status" className="text-sm font-semibold">{sucesso}</p> : null}
            {erroSalvar && !confirmarAberto ? <p role="alert" className="text-sm text-destructive">{erroSalvar}</p> : null}
            {erroCabecalho && !renomearAberto ? <p role="alert" className="text-sm text-destructive">{erroCabecalho}</p> : null}
            {selecionado.ativo ? null : <p className="text-sm text-muted-foreground">Modelo inativo: não aparece em "A partir de um modelo". As peças que o seguem continuam ligadas a ele.</p>}
            {edicao !== null ? (
              <>
                <EditorEtapas etapas={edicao} servicos={servicos} onChange={setEdicao} mostrarErros={mostrarErros} />
                <div className="flex flex-wrap gap-2">
                  <Button type="button" disabled={verificandoImpacto} onClick={() => void pedirSalvar()}>{verificandoImpacto ? 'Verificando...' : 'Salvar'}</Button>
                  <Button type="button" variant="outline" onClick={() => { setEdicao(null); setMostrarErros(false); }}>Cancelar</Button>
                </div>
              </>
            ) : etapasDoSelecionado.length > 0 ? (
              <EditorEtapas etapas={etapasDoSelecionado} servicos={servicos} onChange={() => undefined} somenteLeitura />
            ) : (
              <p className="text-sm text-muted-foreground">Este modelo ainda não tem etapas.</p>
            )}
          </CardContent>
        </Card>
      ) : null}

      <FormDialog open={renomearAberto} onOpenChange={(aberto) => { if (!ocupadoCabecalho) setRenomearAberto(aberto); }}>
        <FormDialogContent>
          <FormDialogHeader>
            <FormDialogTitle>Renomear modelo</FormDialogTitle>
            <FormDialogDescription>O nome aparece em "A partir de um modelo" e na ficha das peças que seguem o modelo.</FormDialogDescription>
          </FormDialogHeader>
          <FormDialogBody>
            <div className="flex flex-col gap-1">
              <label htmlFor="modelo-nome" className="text-sm font-semibold">Nome do modelo</label>
              <Input id="modelo-nome" value={nomeNovo} maxLength={NOME_MAXIMO} onChange={(evento) => setNomeNovo(evento.target.value)} />
            </div>
            {erroCabecalho ? <p role="alert" className="text-sm text-destructive">{erroCabecalho}</p> : null}
          </FormDialogBody>
          <FormDialogFooter>
            <Button type="button" variant="outline" disabled={ocupadoCabecalho} onClick={() => setRenomearAberto(false)}>Voltar</Button>
            <Button type="button" disabled={ocupadoCabecalho || nomeNovo.trim() === ''} onClick={() => void alterarCabecalho({ nome: nomeNovo.trim() }, 'Modelo renomeado.')}>{ocupadoCabecalho ? 'Salvando...' : 'Salvar nome'}</Button>
          </FormDialogFooter>
        </FormDialogContent>
      </FormDialog>

      <FormDialog open={confirmarAberto} onOpenChange={(aberto) => { if (!salvando) setConfirmarAberto(aberto); }}>
        <FormDialogContent>
          <FormDialogHeader>
            <FormDialogTitle>Salvar nova versão do modelo</FormDialogTitle>
            <FormDialogDescription>{impacto ? resumoImpacto(impacto) : ''}</FormDialogDescription>
          </FormDialogHeader>
          <FormDialogBody>
            {impacto && impacto.exemplosQueDiferem.length > 0 ? (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold">Peças com ajuste próprio (não mudam):</p>
                <p className="text-sm text-muted-foreground">
                  {impacto.exemplosQueDiferem.map((peca) => peca.codigo).join(', ')}{impacto.pecasQueDiferem > impacto.exemplosQueDiferem.length ? ' e outras' : ''}
                </p>
              </div>
            ) : null}
            <div className="flex flex-col gap-1">
              <label htmlFor="modelo-motivo" className="text-sm font-semibold">Motivo da alteração</label>
              <Textarea id="modelo-motivo" value={motivo} maxLength={500} rows={3} onChange={(evento) => setMotivo(evento.target.value)} />
              <p className="text-xs text-muted-foreground">Mínimo de {MOTIVO_MINIMO} caracteres. O motivo fica no histórico das peças atualizadas.</p>
            </div>
            {erroSalvar ? <p role="alert" className="text-sm text-destructive">{erroSalvar}</p> : null}
          </FormDialogBody>
          <FormDialogFooter>
            <Button type="button" variant="outline" disabled={salvando} onClick={() => setConfirmarAberto(false)}>Voltar</Button>
            <Button type="button" disabled={salvando || !motivoValido} onClick={() => void confirmarSalvar()}>{salvando ? 'Salvando...' : 'Salvar versão'}</Button>
          </FormDialogFooter>
        </FormDialogContent>
      </FormDialog>
    </div>
  );
}
